import { create } from 'zustand';
import { fetchPresets, type Preset } from '@/lib/api';

interface PresetState {
    /** All loaded presets keyed by app */
    byApp: Record<string, Preset[]>;
    /** Loading state per app */
    loading: Record<string, boolean>;
    /** Whether an app's presets have been fetched at least once */
    loaded: Record<string, boolean>;
    /** Last fetch timestamp per app (ms since epoch) */
    fetchedAt: Record<string, number>;
    /** Fetch presets for a specific app (cached for STALE_TTL_MS, refetches in
     *  background if stale, force=true bypasses the cache entirely) */
    fetchByApp: (app: string, force?: boolean) => Promise<Preset[]>;
    /** Get presets for an app (returns empty array if not loaded) */
    getByApp: (app: string) => Preset[];
    /** Get presets filtered by app + category */
    getByAppAndCategory: (app: string, category: string) => Preset[];
}

// How long the cached list is considered "fresh enough" to skip a refetch.
// Set short so admin deletes/edits propagate to the public app on the next
// page navigation without needing a hard refresh.
const STALE_TTL_MS = 30_000;

let inflightRequests: Record<string, Promise<Preset[]>> = {};

export const usePresetStore = create<PresetState>((set, get) => ({
    byApp: {},
    loading: {},
    loaded: {},
    fetchedAt: {},

    fetchByApp: async (app: string, force = false) => {
        const state = get();

        // Return cached if loaded recently and not forced. Older cached entries
        // fall through to the network — admin edits would otherwise stay
        // invisible until the user hard-refreshed the tab.
        const lastFetch = state.fetchedAt[app] || 0;
        const isFresh = Date.now() - lastFetch < STALE_TTL_MS;
        if (!force && isFresh && state.loaded[app] && state.byApp[app]) {
            return state.byApp[app];
        }

        // Deduplicate concurrent requests for the same app
        if (app in inflightRequests) {
            return inflightRequests[app];
        }

        set((s) => ({
            loading: { ...s.loading, [app]: true },
        }));

        const request = (async () => {
            try {
                const presets = await fetchPresets({ app, limit: 500 });

                set((s) => ({
                    byApp: { ...s.byApp, [app]: presets },
                    loading: { ...s.loading, [app]: false },
                    loaded: { ...s.loaded, [app]: true },
                    fetchedAt: { ...s.fetchedAt, [app]: Date.now() },
                }));

                return presets;
            } catch (error) {
                console.error(`Failed to fetch presets for app "${app}":`, error);
                set((s) => ({
                    loading: { ...s.loading, [app]: false },
                }));
                return state.byApp[app] ?? [];
            } finally {
                delete inflightRequests[app];
            }
        })();

        inflightRequests[app] = request;
        return request;
    },

    getByApp: (app: string) => {
        return get().byApp[app] ?? [];
    },

    getByAppAndCategory: (app: string, category: string) => {
        const presets = get().byApp[app] ?? [];
        return presets.filter((p) => p.category === category);
    },
}));
